/**
 * Subscribers API
 * API client for subscriber management
 */

import api from './auth.api';

// Types
export interface PhoneInfo {
  raw: string | null;
  normalized: string | null;
  formatted: string;
  masked: string;
  valid: boolean;
  validationError?: string | null;
}

export interface StatusInfo {
  raw: string | null;
  normalized: 'eligible' | 'stopped' | 'unknown';
  description: string;
}

export interface PackageInfo {
  id: number | null;
  name: string | null;
}

export interface ZoneInfo {
  id: number | null;
  name: string | null;
}

export interface NodeInfo {
  id: number | null;
  name: string | null;
}

export interface BillingInfo {
  profile: string | null;
  dueDate: string | null;
  expiryDate: string | null;
  /** Sum of due_today + overdue invoice outstanding (used in list table) */
  outstandingAmount: number;
  /** CRM balance snapshot from subscriber sync */
  crmOutstandingAmount?: number;
  dueOverdueAmount?: number;
}

export interface SiteInfo {
  id: number | null;
  crmSiteId: number;
  name: string | null;
}

export interface Subscriber {
  id: number;
  crmSubscriberId: number;
  subscriberCode: string | null;
  username?: string | null;
  portalUsername?: string | null;
  fullname: string | null;
  companyName: string | null;
  email: string | null;
  phone: PhoneInfo;
  status: StatusInfo;
  package: PackageInfo;
  zone: ZoneInfo;
  node: NodeInfo;
  billing: BillingInfo;
  reminderPaused: boolean;
  deletedInCrm: boolean;
  lastSeenInCrm: string | null;
  site: SiteInfo;
  createdAt: string;
  updatedAt: string;
}

export interface ReminderChannelDelivery {
  status: 'sent' | 'failed' | 'skipped' | null;
  lastSentAt: string | null;
  label: string;
}

export interface ReminderDeliverySummary {
  lastReminderSentAt: string | null;
  whatsapp: ReminderChannelDelivery;
  email: ReminderChannelDelivery;
}

export interface SubscriberReminderHistoryItem {
  id: number;
  subscriberId: number;
  invoiceId: number;
  invoiceNumber: string | null;
  channel: string | null;
  source: string | null;
  status: string;
  skipReason: string | null;
  amount: number | string | null;
  sentAt: string | null;
  createdAt: string;
}

export interface SubscriberDetail extends Subscriber {
  username: string | null;
  portalUsername: string | null;
  displayUsername?: string | null;
  networkUsername?: string | null;
  phone: PhoneInfo & { validationError: string | null };
  rawPayload: Record<string, unknown> | null;
  reminderDelivery?: ReminderDeliverySummary;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface SubscribersResponse {
  success: boolean;
  data: {
    subscribers: Subscriber[];
    pagination: PaginationInfo;
  };
}

export interface SubscriberResponse {
  success: boolean;
  data: SubscriberDetail;
}

export interface SyncResult {
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  deleted: number;
  errors: Array<{ subscriberId?: number; error: string }>;
}

export interface SyncStats {
  total: number;
  active: number;
  eligible: number;
  stopped: number;
  withValidPhone: number;
  deleted: number;
  lastSync: string | null;
}

export interface SubscriberSummary {
  total: number;
  statusBreakdown: Record<string, number>;
  phoneStats: {
    valid: number;
    invalid: number;
    missing: number;
  };
}

export interface SubscriberInvoice {
  id: number;
  crmInvoiceId: number;
  invoiceNumber: string | null;
  dates: {
    invoiceDate: string | null;
    dueDate: string | null;
    paidAt: string | null;
  };
  amounts: {
    total: number;
    paid: number;
    outstanding: number;
    currency: string;
  };
  status: {
    paymentStatus: string;
    dueStatus: string;
    reminderPaused: boolean;
  };
  paymentLink: {
    status: string;
    url: string;
    expiresAt: string | null;
  } | null;
  localInvoiceId?: number | null;
  pdf_url?: string | null;
  has_pdf?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriberInvoicesResponse {
  subscriber: { id: number; fullname: string | null };
  invoices: SubscriberInvoice[];
  pagination: PaginationInfo;
}

export interface AppConfigInfo {
  dryRun: boolean;
  features: {
    msg91: {
      configured: boolean;
      email?: { configured: boolean };
    };
  };
}

export interface SubscriberPaymentOrder {
  id: number;
  orderScope?: 'payable' | 'upcoming' | string;
  crmOrderId: string | null;
  paymentUrl: string | null;
  pgRedirectionUrl: string | null;
  totalAmount: number;
  currency: string;
  orderStatus: string;
  adminApprovalStatus: 'pending' | 'approved' | 'rejected' | null;
  adminReviewedAt: string | null;
  gateway: string | null;
  unpaidInvoiceCount: number;
  lastStatusCheckedAt: string | null;
  whatsappLastSentAt: string | null;
  emailLastSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriberPaymentOrderResponse {
  subscriber: { id: number; fullname: string | null };
  paymentOrder: SubscriberPaymentOrder | null;
}

export interface SubscriberPaymentOrdersResponse {
  subscriber: { id: number; fullname: string | null };
  activePaymentOrder: SubscriberPaymentOrder | null;
  aggregateOrders: SubscriberPaymentOrder[];
}

export interface GenerateAllPaymentOrdersResponse {
  queued: boolean;
  eligibleCount: number;
  reason?: string;
  message: string;
}

export interface PaymentOrderGenerationStatusResponse {
  isRunning: boolean;
  eligibleCount: number;
  queue: { waiting: number; active: number; delayed: number };
  message?: string;
  lastResult?: {
    subscribersProcessed: number;
    eligibleCount: number;
    created: number;
    checked: number;
    regenerated: number;
    paid: number;
    skipped: number;
    errors: number;
    errorDetails: Array<{ subscriberId: number; error?: string }>;
  } | null;
}

export interface SubscriberPaymentReminderPreview {
  customerName: string;
  paymentUrl: string;
  totalDue: number;
  currency: string;
  invoices: Array<{
    invoiceNumber: string;
    amount: number;
    dueDate: string | null;
    dueStatus: string;
  }>;
  messageText: string;
}

export interface ReminderChannelResult {
  status: 'sent' | 'skipped' | 'failed';
  skipReason?: 'no_phone' | 'no_email';
  attemptId?: number;
  messageParams?: unknown;
  emailPreview?: { to: string; subject: string; body: string };
  error?: string;
}

export interface SendPaymentReminderResponse {
  attemptId?: number;
  dryRun: boolean;
  whatsapp: ReminderChannelResult;
  email: ReminderChannelResult;
  messageParams?: unknown;
  messagePreview?: SubscriberPaymentReminderPreview;
  status: string;
  error?: string;
}

function isReminderChannelResult(value: unknown): value is ReminderChannelResult {
  return (
    value != null &&
    typeof value === 'object' &&
    'status' in value &&
    ['sent', 'skipped', 'failed'].includes(String((value as ReminderChannelResult).status))
  );
}

function normalizePaymentReminderResponse(
  raw: Partial<SendPaymentReminderResponse> & Record<string, unknown>
): SendPaymentReminderResponse {
  const legacyOk = raw.status === 'sent' || raw.status === 'dry_run';
  const whatsapp: ReminderChannelResult = isReminderChannelResult(raw.whatsapp)
    ? raw.whatsapp
    : {
        status: legacyOk ? 'sent' : raw.status === 'failed' ? 'failed' : 'skipped',
        messageParams: raw.messageParams,
        error: raw.error,
      };
  const email: ReminderChannelResult = isReminderChannelResult(raw.email)
    ? raw.email
    : isReminderChannelResult(raw.whatsapp)
      ? {
          status: 'skipped',
          error: 'Email channel missing from API response. Restart the backend server.',
        }
      : legacyOk
        ? { status: 'skipped', skipReason: 'no_email' }
        : { status: 'failed', error: raw.error };

  return {
    attemptId: raw.attemptId,
    dryRun: raw.dryRun ?? false,
    whatsapp,
    email,
    messageParams: raw.messageParams,
    messagePreview: raw.messagePreview,
    status: raw.status ?? (legacyOk ? 'sent' : 'failed'),
    error: raw.error,
  };
}

// Query params interface
export interface ListSubscribersParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'eligible' | 'stopped' | 'unknown';
  phone_valid?: boolean;
  has_outstanding?: boolean;
  reminder_paused?: boolean;
  site_id?: number;
  package_id?: number;
  zone_id?: number;
  node_id?: number;
  include_deleted?: boolean;
}

// API functions
export const subscribersApi = {
  /**
   * List subscribers with filters and pagination
   */
  list: async (params: ListSubscribersParams = {}): Promise<SubscribersResponse['data']> => {
    const response = await api.get<SubscribersResponse>('/subscribers', { params });
    return response.data.data;
  },

  /**
   * Get subscriber details
   */
  get: async (id: number | string): Promise<SubscriberDetail> => {
    const response = await api.get<SubscriberResponse>(`/subscribers/${id}`);
    return response.data.data;
  },

  getReminderHistory: async (
    id: number | string,
    params?: { page?: number; limit?: number }
  ): Promise<{
    reminders: SubscriberReminderHistoryItem[];
    pagination: PaginationInfo;
  }> => {
    const response = await api.get<{
      success: boolean;
      data: {
        reminders: SubscriberReminderHistoryItem[];
        pagination: { page: number; perPage: number; total: number; totalPages: number };
      };
    }>(`/subscribers/${id}/reminder-history`, { params });
    const { reminders, pagination } = response.data.data;
    return {
      reminders,
      pagination: {
        page: pagination.page,
        limit: pagination.perPage,
        total: pagination.total,
        totalPages: pagination.totalPages,
      },
    };
  },

  /**
   * Sync all subscribers from CRM
   */
  bulkSync: async (siteId?: number, incremental = false): Promise<{ message: string; siteId: number; incremental: boolean }> => {
    const response = await api.post('/subscribers/sync', {
      site_id: siteId,
      incremental,
    });
    return response.data.data;
  },

  /**
   * Sync single subscriber from CRM
   */
  syncSingle: async (id: number | string): Promise<{ action: 'created' | 'updated'; subscriber: Partial<Subscriber> }> => {
    const response = await api.post(`/subscribers/${id}/sync`);
    return response.data.data;
  },

  /**
   * Pause reminders for a subscriber
   */
  pauseReminders: async (id: number | string, reason?: string): Promise<{ id: number; reminderPaused: boolean; reason?: string }> => {
    const response = await api.post(`/subscribers/${id}/pause-reminders`, { reason });
    return response.data.data;
  },

  /**
   * Resume reminders for a subscriber
   */
  resumeReminders: async (id: number | string): Promise<{ id: number; reminderPaused: boolean }> => {
    const response = await api.post(`/subscribers/${id}/resume-reminders`);
    return response.data.data;
  },

  /**
   * Get sync statistics
   */
  getSyncStats: async (siteId?: number): Promise<SyncStats> => {
    const response = await api.get('/subscribers/sync/stats', {
      params: siteId ? { site_id: siteId } : undefined,
    });
    return response.data.data;
  },

  /**
   * Get subscriber summary
   */
  getSummary: async (siteId?: number): Promise<SubscriberSummary> => {
    const response = await api.get('/subscribers/summary', {
      params: siteId ? { site_id: siteId } : undefined,
    });
    return response.data.data;
  },

  /**
   * Toggle reminder pause for a subscriber
   */
  togglePause: async (
    id: number | string
  ): Promise<{ id: number; reminderPaused: boolean }> => {
    const response = await api.post(`/subscribers/${id}/toggle-pause`);
    return response.data.data;
  },

  /**
   * Get invoices for a subscriber
   */
  getInvoices: async (
    id: number | string,
    params?: { page?: number; limit?: number; due_status?: string; status?: string }
  ): Promise<SubscriberInvoicesResponse> => {
    const response = await api.get<{ success: boolean; data: SubscriberInvoicesResponse }>(
      `/subscribers/${id}/invoices`,
      { params }
    );
    return response.data.data;
  },

  generateAllPaymentOrders: async (): Promise<GenerateAllPaymentOrdersResponse> => {
    const response = await api.post<{
      success: boolean;
      data: GenerateAllPaymentOrdersResponse;
    }>('/subscribers/payment-orders/generate');
    return response.data.data;
  },

  getPaymentOrderGenerationStatus: async (): Promise<PaymentOrderGenerationStatusResponse> => {
    const response = await api.get<{
      success: boolean;
      data: PaymentOrderGenerationStatusResponse;
    }>('/subscribers/payment-orders/generate/status');
    return response.data.data;
  },

  generatePaymentOrder: async (
    id: number | string
  ): Promise<{
    action: string;
    error?: string;
    paymentOrder: SubscriberPaymentOrder | null;
  }> => {
    const response = await api.post<{
      success: boolean;
      data: {
        action: string;
        error?: string;
        paymentOrder: SubscriberPaymentOrder | null;
      };
    }>(`/subscribers/${id}/payment-order/generate`);
    return response.data.data;
  },

  getPaymentOrders: async (id: number | string): Promise<SubscriberPaymentOrdersResponse> => {
    const response = await api.get<{ success: boolean; data: SubscriberPaymentOrdersResponse }>(
      `/subscribers/${id}/payment-orders`
    );
    return response.data.data;
  },

  getPaymentOrder: async (id: number | string): Promise<SubscriberPaymentOrderResponse> => {
    const response = await api.get<{ success: boolean; data: SubscriberPaymentOrderResponse }>(
      `/subscribers/${id}/payment-order`
    );
    return response.data.data;
  },

  reviewPaymentOrder: async (
    subscriberId: number | string,
    orderId: number | string,
    approved: boolean
  ): Promise<{
    action: 'approved' | 'rejected';
    message: string;
    paymentOrder: SubscriberPaymentOrder;
    newPaymentOrder: SubscriberPaymentOrder | null;
  }> => {
    const response = await api.post<{
      success: boolean;
      data: {
        action: 'approved' | 'rejected';
        message: string;
        paymentOrder: SubscriberPaymentOrder;
        newPaymentOrder: SubscriberPaymentOrder | null;
      };
    }>(`/subscribers/${subscriberId}/payment-orders/${orderId}/review`, { approved });
    return response.data.data;
  },

  sendPaymentReminder: async (
    id: number | string,
    dryRun = false
  ): Promise<SendPaymentReminderResponse> => {
    const response = await api.post<{ success: boolean; data: Partial<SendPaymentReminderResponse> }>(
      `/subscribers/${id}/send-payment-reminder`,
      { dryRun }
    );
    return normalizePaymentReminderResponse(response.data.data ?? {});
  },

  getAppConfig: async (): Promise<AppConfigInfo> => {
    const base = import.meta.env.VITE_API_URL || 'http://localhost:4000';
    const token = localStorage.getItem('token');
    const response = await fetch(`${base}/api`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new Error('Failed to load app config');
    }
    const json = (await response.json()) as { data: AppConfigInfo };
    return json.data;
  },
};

export default subscribersApi;
